/**
 * Standalone Trashboard subpage (/trashboard) for live monitoring.
 *
 * Displays top 100 scores in Space Impact monochrome style with full mobile/desktop support.
 * Supports offline score viewing & sync with Railway DB.
 */
import { fetchTopScores, isApiConfigured, syncOfflineScores, type ScoreRow } from "../lib/api";
import { getPendingScoresCount } from "../lib/offlineStore";

const REFRESH_INTERVAL_MS = 30000; // 30 seconds
let _refreshTimer: ReturnType<typeof setInterval> | null = null;

export async function mountTrashboardPage(root: HTMLElement | null): Promise<void> {
  if (!root) return;

  if (_refreshTimer) {
    clearInterval(_refreshTimer);
    _refreshTimer = null;
  }

  root.innerHTML = `
    <div class="trashboard-page">
      <div class="trashboard-card">
        <div class="trashboard-header">
          <h1>TRASHBOARD</h1>
          <div class="subtitle">TOP 100 RECYCLERS &bull; TRASHSTOCK &times; OFF FESTIVAL</div>
          <div class="prize-banner contest-finished">🏆 CONTEST FINISHED — TOP 3 WINNERS ANNOUNCED 🏆</div>
          <div class="trashboard-live-bar">
            <span class="live-dot"></span> FINAL LEADERBOARD &bull; REFRESH: 30S &bull; UPDATED <span id="trashboard-last-updated">--:--:--</span>
          </div>
          <div id="trashboard-sync-bar" style="margin-top: 8px;"></div>
        </div>

        <div class="trashboard-table-wrapper">
          <div id="trashboard-content">
            <div class="trashboard-status">LOADING TOP 100 SCORES…</div>
          </div>
        </div>
      </div>
    </div>
  `;

  await loadScores(root, true);

  // Auto-refresh every 30 seconds for live monitoring display
  _refreshTimer = setInterval(() => {
    void loadScores(root, false);
  }, REFRESH_INTERVAL_MS);
}

async function loadScores(root: HTMLElement, isInitial = false): Promise<void> {
  const contentEl = root.querySelector<HTMLElement>("#trashboard-content");
  const timeEl = root.querySelector<HTMLElement>("#trashboard-last-updated");
  const syncBarEl = root.querySelector<HTMLElement>("#trashboard-sync-bar");
  if (!contentEl) return;

  if (isInitial) {
    contentEl.innerHTML = `<div class="trashboard-status">LOADING TOP 100 SCORES…</div>`;
  }

  const isOnline = await isApiConfigured();
  const pendingCount = getPendingScoresCount();

  if (syncBarEl) {
    if (pendingCount > 0) {
      syncBarEl.innerHTML = `
        <div style="font-size: 11px; color: #ffff00; text-align: center;">
          ⚠️ ${pendingCount} OFFLINE SCORE(S) STORED LOCALLY
          <button id="trashboard-sync-btn" style="margin-left: 8px; padding: 4px 8px; font-size: 10px; font-family: inherit; background: #ffff00; color: #000; border: none; font-weight: bold; cursor: pointer;">SYNC TO RAILWAY</button>
        </div>
      `;
      const syncBtn = syncBarEl.querySelector<HTMLButtonElement>("#trashboard-sync-btn");
      syncBtn?.addEventListener("click", async () => {
        syncBtn.disabled = true;
        syncBtn.textContent = "SYNCING...";
        const res = await syncOfflineScores();
        if (res.success) {
          alert(`Synced ${res.count} score(s) to Railway!`);
          void loadScores(root, false);
        } else {
          alert(`Sync failed: ${res.error ?? "Server unreachable"}`);
          syncBtn.disabled = false;
          syncBtn.textContent = "SYNC TO RAILWAY";
        }
      });
    } else if (!isOnline) {
      syncBarEl.innerHTML = `
        <div style="font-size: 10px; color: #ff8888; text-align: center;">
          📡 OFFLINE MODE — SHOWING LOCAL CACHED SCORES
        </div>
      `;
    } else {
      syncBarEl.innerHTML = "";
    }
  }

  try {
    const rows: ScoreRow[] = await fetchTopScores(100);
    if (rows.length === 0) {
      contentEl.innerHTML = `
        <div class="trashboard-status">
          NO SCORES YET.<br/><br/>
          WAITING FOR RUNS…
        </div>
      `;
    } else {
      const tableHtml = `
        <table class="trashboard-table">
          <thead>
            <tr>
              <th class="trashboard-rank">#</th>
              <th>CALLSIGN</th>
              <th style="text-align: center;">WAVE</th>
              <th class="trashboard-score">SCORE</th>
            </tr>
          </thead>
          <tbody>
            ${rows
              .map((r, i) => {
                const rankStr = (i + 1).toString().padStart(i >= 99 ? 3 : 2, "0");
                const topClass = i === 0 ? "top-1" : i === 1 ? "top-2" : i === 2 ? "top-3" : "";
                const badge = i === 0 ? " 🥇 1st" : i === 1 ? " 🥈 2nd" : i === 2 ? " 🥉 3rd" : "";
                const trophy = r.completed ? " 🏆" : "";
                const callsign = escapeHtml(r.display_name);
                const scoreStr = r.score.toLocaleString();
                const waveStr = r.wave ? `W${r.wave}` : "-";

                return `
                  <tr class="${topClass}">
                    <td class="trashboard-rank">${rankStr}.</td>
                    <td class="trashboard-callsign">${callsign}${trophy}${badge}</td>
                    <td class="trashboard-wave">${waveStr}</td>
                    <td class="trashboard-score">${scoreStr}</td>
                  </tr>
                `;
              })
              .join("")}
          </tbody>
        </table>
      `;

      contentEl.innerHTML = tableHtml;
    }

    if (timeEl) {
      const now = new Date();
      timeEl.textContent = now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
    }
  } catch (err) {
    if (isInitial) {
      contentEl.innerHTML = `
        <div class="trashboard-status">
          ERROR LOADING SCORES<br/><br/>
          <span style="font-size: 11px; opacity: 0.7;">${escapeHtml((err as Error).message)}</span>
        </div>
      `;
    }
  }
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}
